import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  LayoutDashboard, UserCircle, FileText, Upload,
  CheckCircle, Circle, Save, FileUp, ShieldCheck, Loader2, Download
} from "lucide-react";
import { toast } from "sonner";

const navItems = [
  { id: "dashboard", label: "Dashboard", path: "/ngo-dashboard", icon: LayoutDashboard },
  { id: "profile", label: "Profile", path: "/ngo-dashboard", icon: UserCircle },
  { id: "documents", label: "Documents", path: "/ngo-dashboard", icon: FileText },
];

function DocumentUploadZone({ docType, label, isVerified, existingDoc, onUploadSuccess, api }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files are accepted");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be under 10MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("doc_type", docType);
      const { data } = await api.post("/ngo/documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${label} uploaded and verified`);
      onUploadSuccess(data);
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  if (isVerified && existingDoc) {
    return (
      <div
        className="border border-emerald-200 bg-emerald-50/50 rounded-lg p-5 flex items-center justify-between"
        data-testid={`doc-zone-${docType}-verified`}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-slate-900">{label}</p>
              <Badge className="bg-emerald-600 text-white border-0 text-[10px] px-1.5 py-0 h-5 gap-1" data-testid={`badge-verified-${docType}`}>
                <CheckCircle className="w-3 h-3" />
                Verified
              </Badge>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">{existingDoc.filename} &middot; {formatSize(existingDoc.size)}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-slate-500 hover:text-emerald-800"
          onClick={() => {
            const link = document.createElement("a");
            link.href = `${api.defaults.baseURL}/ngo/documents/${existingDoc.id}/download`;
            link.download = existingDoc.filename;
            // Use fetch to handle cookie auth
            api.get(`/ngo/documents/${existingDoc.id}/download`, { responseType: "blob" })
              .then(res => {
                const url = URL.createObjectURL(res.data);
                link.href = url;
                link.click();
                URL.revokeObjectURL(url);
              })
              .catch(() => toast.error("Download failed"));
          }}
          data-testid={`doc-download-${docType}`}
        >
          <Download className="w-4 h-4 mr-1" />
          Download
        </Button>
      </div>
    );
  }

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-6 text-center transition-all cursor-pointer ${
        dragOver
          ? "border-emerald-500 bg-emerald-50/50"
          : "border-slate-200 bg-slate-50/30 hover:border-slate-300"
      }`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onClick={() => !uploading && fileRef.current?.click()}
      data-testid={`doc-zone-${docType}`}
    >
      <input
        ref={fileRef}
        type="file"
        accept=".pdf,application/pdf"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`doc-input-${docType}`}
      />
      <div className="flex flex-col items-center">
        {uploading ? (
          <>
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin mb-3" />
            <p className="text-sm font-medium text-slate-600">Uploading {label}...</p>
          </>
        ) : (
          <>
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center mb-3">
              <FileUp className="w-5 h-5 text-slate-400" />
            </div>
            <p className="text-sm font-medium text-slate-700">{label}</p>
            <p className="text-xs text-slate-400 mt-1">
              Drag & drop or click to upload &middot; PDF only &middot; Max 10MB
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function formatSize(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const SECTOR_OPTIONS = [
  "Healthcare", "Education", "Environment", "Livelihoods",
  "Women Empowerment", "Rural Development", "Sanitation", "Technology"
];

export default function NgoDashboard() {
  const { api } = useAuth();
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    org_name: "",
    registration_number: "",
    focus_sectors: [],
  });

  useEffect(() => {
    Promise.all([loadProfile(), loadDocuments()]).finally(() => setLoading(false));
  }, []);

  const loadProfile = async () => {
    try {
      const { data } = await api.get("/ngo/profile");
      setProfile(data);
      setForm({
        org_name: data.org_name || "",
        registration_number: data.registration_number || "",
        focus_sectors: data.focus_sectors || [],
      });
    } catch (e) {
      console.error("Failed to load profile", e);
    }
  };

  const loadDocuments = async () => {
    try {
      const { data } = await api.get("/ngo/documents");
      setDocuments(data);
    } catch (e) {
      console.error("Failed to load documents", e);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/ngo/profile", form);
      setProfile(data);
      toast.success("Profile updated successfully");
    } catch (e) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleDocUploadSuccess = async () => {
    // Refresh both profile (for updated status) and documents
    await Promise.all([loadProfile(), loadDocuments()]);
  };

  const doc80g = documents.find((d) => d.doc_type === "80g");
  const doc12a = documents.find((d) => d.doc_type === "12a");

  // Completion: org_name, registration_number, focus_sectors, 80g status, 12a status
  const completionFields = [
    form.org_name,
    form.registration_number,
    form.focus_sectors.length > 0,
    profile?.status_80g,
    profile?.status_12a,
  ];
  const filledCount = completionFields.filter(Boolean).length;
  const completionPercent = Math.round((filledCount / completionFields.length) * 100);

  if (loading) {
    return (
      <DashboardLayout navItems={navItems}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={navItems}>
      <div className="max-w-4xl space-y-8" data-testid="ngo-dashboard">
        {/* Welcome Header */}
        <div>
          <h2 className="font-sans text-2xl font-semibold text-slate-900 tracking-tight">
            Welcome back
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Complete your profile to get discovered by CSR teams.
          </p>
        </div>

        {/* Profile Setup Card */}
        <Card className="bg-white border border-slate-200 rounded-lg shadow-sm" data-testid="ngo-profile-setup-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-sans text-lg text-slate-900">Profile Setup</CardTitle>
                <CardDescription className="text-slate-500 mt-1">
                  Fill in your organization details for verification
                </CardDescription>
              </div>
              <Badge
                className={
                  completionPercent === 100
                    ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                    : "bg-amber-50 text-amber-700 border-amber-200"
                }
              >
                {completionPercent}% Complete
              </Badge>
            </div>
            <Progress value={completionPercent} className="mt-4 h-1.5 bg-slate-100 [&>div]:bg-emerald-600" />
          </CardHeader>

          <Separator />

          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-sm font-medium text-slate-700">Organization Name</Label>
                <Input
                  value={form.org_name}
                  onChange={(e) => setForm({ ...form, org_name: e.target.value })}
                  placeholder="Enter organization name"
                  className="mt-1.5 focus-visible:ring-emerald-600"
                  data-testid="ngo-org-name-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Registration Number</Label>
                <Input
                  value={form.registration_number}
                  onChange={(e) => setForm({ ...form, registration_number: e.target.value })}
                  placeholder="e.g., NGO-KL-2024-001"
                  className="mt-1.5 focus-visible:ring-emerald-600"
                  data-testid="ngo-reg-number-input"
                />
              </div>
            </div>

            {/* Focus Sectors */}
            <div className="mt-6">
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Focus Sectors</Label>
              <div className="flex flex-wrap gap-2">
                {SECTOR_OPTIONS.map((sector) => (
                  <button
                    key={sector}
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        focus_sectors: prev.focus_sectors.includes(sector)
                          ? prev.focus_sectors.filter((s) => s !== sector)
                          : [...prev.focus_sectors, sector],
                      }))
                    }
                    className={`text-xs px-3 py-1.5 rounded-md border transition-all ${
                      form.focus_sectors.includes(sector)
                        ? "border-emerald-600 bg-emerald-50 text-emerald-800 font-medium"
                        : "border-slate-200 text-slate-500 hover:border-slate-300"
                    }`}
                    data-testid={`ngo-sector-${sector.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {sector}
                  </button>
                ))}
              </div>
            </div>

            {/* Compliance Status (read-only, driven by document uploads) */}
            <div className="mt-6">
              <Label className="text-sm font-medium text-slate-700 mb-3 block">Compliance Status</Label>
              <div className="flex flex-wrap gap-4">
                <div
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-md border text-sm ${
                    profile?.status_80g
                      ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                      : "border-slate-200 text-slate-400"
                  }`}
                  data-testid="ngo-80g-status"
                >
                  {profile?.status_80g ? <CheckCircle className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                  80G Certificate
                  {profile?.status_80g && (
                    <Badge className="bg-emerald-600 text-white border-0 text-[10px] px-1.5 py-0 h-5 ml-1">Verified</Badge>
                  )}
                </div>
                <div
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-md border text-sm ${
                    profile?.status_12a
                      ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                      : "border-slate-200 text-slate-400"
                  }`}
                  data-testid="ngo-12a-status"
                >
                  {profile?.status_12a ? <CheckCircle className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                  12A Registration
                  {profile?.status_12a && (
                    <Badge className="bg-emerald-600 text-white border-0 text-[10px] px-1.5 py-0 h-5 ml-1">Verified</Badge>
                  )}
                </div>
              </div>
              {!profile?.status_80g && !profile?.status_12a && (
                <p className="text-xs text-slate-400 mt-2">Upload documents below to verify compliance status</p>
              )}
            </div>

            {/* X-Score */}
            {profile && (
              <div className="mt-6 p-4 bg-slate-50 rounded-md border border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-700">X-Score</p>
                    <p className="text-xs text-slate-500 mt-0.5">Trust score calculated by ImpactX Bridge</p>
                  </div>
                  <span className="font-sans text-2xl font-bold text-emerald-900" data-testid="ngo-x-score">
                    {profile.x_score}
                  </span>
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <Button
                className="bg-ix-red-600 text-white hover:bg-ix-red-700 shadow-sm"
                onClick={handleSave}
                disabled={saving}
                data-testid="ngo-save-profile-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save Profile"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Document Vault */}
        <Card className="bg-white border border-slate-200 rounded-lg shadow-sm" data-testid="ngo-document-upload-card">
          <CardHeader>
            <CardTitle className="font-sans text-lg text-slate-900">Document Vault</CardTitle>
            <CardDescription className="text-slate-500">
              Upload compliance certificates to verify your NGO status. PDF format only.
            </CardDescription>
          </CardHeader>
          <Separator />
          <CardContent className="pt-6 space-y-4" data-testid="ngo-document-upload-zone">
            <DocumentUploadZone
              docType="80g"
              label="80G Certificate"
              isVerified={profile?.status_80g}
              existingDoc={doc80g}
              onUploadSuccess={handleDocUploadSuccess}
              api={api}
            />
            <DocumentUploadZone
              docType="12a"
              label="12A Certificate"
              isVerified={profile?.status_12a}
              existingDoc={doc12a}
              onUploadSuccess={handleDocUploadSuccess}
              api={api}
            />
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
